import { useEffect, useMemo, useState } from 'react'
import { UserIcon, EyeIcon, StarIcon } from '@heroicons/react/24/outline'
import {
  AML_CATEGORY_TABS,
  resolveResultsByCategory,
  formatThreatLevel
} from '../../utils/amlResultCategories'
import { getMatchCardHeadline, getMatchCardSubtitle } from '../../utils/amlMatchPresentation'
import { isWatchlistAmlProvider, sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'
import GreenIdScreeningSummary from './GreenIdScreeningSummary'

/**
 * Tabbed AML search results with match cards for the active category.
 */
export default function AmlCategorizedSearchResults({
  persons = [],
  resultsByCategory = null,
  provider = null,
  greenidVerificationId = null,
  greenidOverallStatus = null,
  greenidProfilesMatchedCount = null,
  screeningSummary = null,
  onViewDetails,
  getRiskBadgeColor,
  getRiskLevelColor,
  getConfidenceColor,
  getThreatLevelColor
}) {
  const grouped = useMemo(
    () => resolveResultsByCategory(persons, resultsByCategory),
    [persons, resultsByCategory]
  )

  const firstNonEmptyTab = useMemo(() => {
    const tab = AML_CATEGORY_TABS.find((t) => (grouped[t.key]?.length || 0) > 0)
    return tab?.key || 'pep'
  }, [grouped])

  const [activeTab, setActiveTab] = useState(firstNonEmptyTab)
  useEffect(() => setActiveTab(firstNonEmptyTab), [firstNonEmptyTab, persons])

  const activeList = grouped[activeTab] || []
  const totalCount = AML_CATEGORY_TABS.reduce((n, t) => n + (grouped[t.key]?.length || 0), 0)

  if (totalCount === 0) return null

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
        <UserIcon className="h-5 w-5 text-gray-600 mr-2" />
        Screening matches ({totalCount})
      </h3>

      {isWatchlistAmlProvider(screeningSummary?.provider) ? (
        <GreenIdScreeningSummary summary={screeningSummary} />
      ) : (
        (greenidVerificationId || greenidProfilesMatchedCount != null || greenidOverallStatus) && (
          <div className="text-xs text-gray-500 border border-gray-200 rounded-md p-2 bg-gray-50 mb-3">
            {greenidVerificationId && (
              <span className="mr-3">
                Screening reference: <strong>{greenidVerificationId}</strong>
              </span>
            )}
            {greenidProfilesMatchedCount != null && (
              <span className="mr-3">
                Structured profiles matched: <strong>{greenidProfilesMatchedCount}</strong>
              </span>
            )}
            {greenidOverallStatus && (
              <span>
                Overall: <strong>{formatThreatLevel(greenidOverallStatus)}</strong>
              </span>
            )}
          </div>
        )
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3 mb-4">
        {AML_CATEGORY_TABS.map((tab) => {
          const count = grouped[tab.key]?.length || 0
          if (count === 0) return null
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded ${activeTab === tab.key ? 'bg-primary-500' : 'bg-gray-100'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      <div className="space-y-3">
        {activeList.map((person, idx) => (
          <PersonMatchCard
            key={`person-${person.id ?? 'row'}-${idx}`}
            person={person}
            onViewDetails={onViewDetails}
            getRiskBadgeColor={getRiskBadgeColor}
            getRiskLevelColor={getRiskLevelColor}
            getConfidenceColor={getConfidenceColor}
            getThreatLevelColor={getThreatLevelColor}
          />
        ))}
      </div>
    </div>
  )
}

function PersonMatchCard({
  person,
  onViewDetails,
  getRiskBadgeColor,
  getRiskLevelColor,
  getConfidenceColor,
  getThreatLevelColor
}) {
  const threatDisplay = formatThreatLevel(person.threatLevel)
  const headline = getMatchCardHeadline(person)
  const cardSubtitle = getMatchCardSubtitle(person)
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">{headline}</h4>
            {person.detailKindLabel && (
              <span className="px-2 py-1 text-xs rounded border bg-slate-100 text-slate-700">
                {person.detailKindLabel}
              </span>
            )}
            <span className={`px-2 py-1 text-xs font-medium rounded border ${getRiskBadgeColor(person.riskCategory)}`}>
              {person.riskCategory}
            </span>
            {person.riskLevel && (
              <span className={`px-2 py-1 text-xs font-bold rounded border ${getRiskLevelColor(person.riskLevel)}`}>
                {person.riskLevel}
              </span>
            )}
            <div className={`flex items-center text-sm font-medium ${getConfidenceColor(person.confidence)}`}>
              <StarIcon className="h-4 w-4 mr-1" />
              {person.confidence}% match
            </div>
          </div>
          <div className="mb-2">
            {cardSubtitle && (
              <p className="text-sm text-gray-700 mb-1 line-clamp-3">{cardSubtitle}</p>
            )}
            <p className="text-sm text-gray-500">{person.riskDescription}</p>
            {person.profileNotes && person.matchKind !== 'pep_group' && (
              <p className="text-xs text-amber-800 mt-1 bg-amber-50 border border-amber-100 rounded p-2 line-clamp-2">
                {sanitizeVendorBranding(person.profileNotes)}
              </p>
            )}
            {Array.isArray(person.aliases) && person.aliases.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">Aliases: {person.aliases.join(', ')}</p>
            )}
            {person.threatLevel && (
              <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded border ${getThreatLevelColor(threatDisplay)}`}>
                {threatDisplay}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onViewDetails?.(person)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center ml-4 shrink-0"
        >
          <EyeIcon className="h-4 w-4 mr-1" />
          Details
        </button>
      </div>
    </div>
  )
}
